/**
 * localize-notice — the one sentence a copy field owes an operator about its
 * other languages, under the field where a sentence belongs.
 *
 * The trigger it explains now lives inside the field's own `actions` slot, and
 * that slot takes controls: on a single-line field it is an absolutely
 * positioned band at the trailing edge whose measured width the field gives up
 * from its text inset, so a paragraph there overflows the box and eats the
 * value it sits beside. The control went in; the sentence stayed out.
 *
 * ONE LINE, NOT THREE. The three answers are ordered by which one the operator
 * must act on first — an unsaved edit outranks a failed pass, and both outrank
 * a field with nothing in it — so at most one renders and the field never
 * carries two instructions at once. `display: contents` keeps a silent notice
 * out of the form's own gap, which a zero-height box would double.
 *
 * The empty-field answer reads `nothingToLocalize`, the same call the trigger
 * disables itself on, so the two can never disagree and no mounting form
 * re-derives either.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { nothingToLocalize } from "./localized-text.view.ts";

@schmancyElement("localize-notice")
export class LocalizeNotice extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: contents;
			}
		`,
	];

	/** The STORED source string the versions were, or would be, written from. */
	@property() source = "";
	/** The field carries an edit this form has not committed yet — a pass would
	 *  read the old words and this form's own Save would retire the result. */
	@property({ type: Boolean }) unsaved = false;
	/** The last pass wrote nothing, and nothing re-attempts it on its own. */
	@property({ type: Boolean }) failed = false;

	override render() {
		if (this.unsaved)
			return html`
				<schmancy-typography
					type="body"
					token="sm"
					class="max-w-prose text-error-default"
				>
					${this.t("admin.events.localize.fieldSaveFirstHint")}
				</schmancy-typography>
			`;
		// A failed pass is the operator's only word that the spinner went out for
		// good, and it lands unbidden on a form they are already sitting on —
		// which is what role="status" carries to a reader who is not looking.
		if (this.failed)
			return html`
				<schmancy-typography
					role="status"
					type="body"
					token="sm"
					class="max-w-prose text-error-default"
				>
					${this.t("admin.events.localize.fieldTranslateFailedHint")}
				</schmancy-typography>
			`;
		if (nothingToLocalize(this.source))
			return html`
				<schmancy-typography
					type="body"
					token="sm"
					class="max-w-prose text-surface-onVariant"
				>
					${this.t("admin.events.localize.fieldNothingStoredHint")}
				</schmancy-typography>
			`;
		return nothing;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"localize-notice": LocalizeNotice;
	}
}
